'use client'

import { useState, useEffect, useRef } from 'react'
import { useSession } from 'next-auth/react'
import Link from 'next/link'
import { FglHeader } from '@/components/layout/fgl-header'
import { useTranslation } from '@/i18n/client'
import { Turnstile } from '@/components/security/turnstile'
import { ArrowLeft, MapPin, CheckCircle, AlertCircle, Lock } from 'lucide-react'

// Hierarchischer Country → Regions Map. Ein Pilot waehlt zuerst Land/Kontinent,
// dann erscheinen die jeweiligen Sub-Regionen. „International" deckt alles ab,
// was ausserhalb der gepflegten Regionen liegt.
const COUNTRY_REGIONS: Record<string, string[]> = {
  'Deutschland': [
    'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
    'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen',
    'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen',
  ],
  'Österreich': [
    'Burgenland', 'Kärnten', 'Niederösterreich', 'Oberösterreich', 'Salzburg',
    'Steiermark', 'Tirol', 'Vorarlberg', 'Wien',
  ],
  'Schweiz': [
    'Zürich', 'Bern', 'Luzern', 'Uri', 'Schwyz', 'Obwalden', 'Nidwalden', 'Glarus',
    'Zug', 'Freiburg', 'Solothurn', 'Basel-Stadt', 'Basel-Landschaft',
    'Schaffhausen', 'Appenzell', 'St. Gallen', 'Graubünden', 'Aargau',
    'Thurgau', 'Tessin', 'Waadt', 'Wallis', 'Neuenburg', 'Genf', 'Jura',
  ],
  'Vereinigtes Königreich': ['England', 'Schottland', 'Wales', 'Nordirland'],
  'Italien': [],
  'Frankreich': [],
  'Niederlande': [],
  'Belgien': [],
  'Luxemburg': [],
  'Spanien': [],
  'Portugal': [],
  'Polen': [],
  'Tschechien': [],
  'Slowakei': [],
  'Slowenien': [],
  'Ungarn': [],
  'Schweden': [],
  'Norwegen': [],
  'Finnland': [],
  'Dänemark': [],
  'Australien': [],
  'Neuseeland': [],
  'USA': [],
  'Kanada': [],
  'International': [],
}

const COUNTRY_OPTIONS = Object.keys(COUNTRY_REGIONS)

// 2026-05-29 (R100.54): Rollen reduziert auf nur "Örtlicher Ansprechpartner".
// Die alten Rollen (Regionalleiter / Vereinskontakt / Wettbewerbsleiter / etc.)
// waren für Bewerber missverständlich — Stephans Vorgabe: einfach + datensparsam.
// Falls künftig wieder differenziert werden muss, übernehmen wir das über die
// Schwerpunkte (Focus-Areas) statt über separate Rollen.
const ROLES = [
  'Örtlicher Ansprechpartner',
]

// Schwerpunkte sind die Themen, in denen der Ansprechpartner Pilotinnen und
// Piloten unterstuetzen kann. Mehrfachauswahl, optional. Wird hinten an die
// `role`-Bezeichnung angehaengt (Format: „Regionalleiter · Fliegen · Technik")
// — bis ein eigenes focus_areas-Feld in der DB existiert (Phase 2).
const FOCUS_AREAS = [
  { value: 'Fliegen', label: 'Fliegen', tip: 'Praxis am Hang/Flachland — Thermik finden, Sollfahrt, Rückkehr' },
  { value: 'Technik', label: 'Technik', tip: 'Modellbau, Setup, Logger, Servos, RC-Anlage, Motor/Antrieb' },
  { value: 'Tipps und Tricks', label: 'Tipps & Tricks', tip: 'Allgemeine Erfahrung, Vereins-Insider, Wettbewerbs-Vorbereitung, Wetter-Bewertung' },
]

// Mini-Karte zum Standort-Klicken
function LocationPicker({ lat, lon, onLocationChange }: {
  lat: number | null
  lon: number | null
  onLocationChange: (lat: number, lon: number) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    import('leaflet').then((L) => {
      if (!document.querySelector('link[href*="leaflet.css"]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
        document.head.appendChild(link)
      }

      const map = L.map(containerRef.current!, {
        center: [lat || 51.1657, lon || 10.4515],
        zoom: lat ? 10 : 6,
        zoomControl: true,
      })

      L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OSM',
        maxZoom: 18,
      }).addTo(map)

      // Click handler
      map.on('click', (e: any) => {
        const { lat, lng } = e.latlng
        onLocationChange(Math.round(lat * 10000) / 10000, Math.round(lng * 10000) / 10000)

        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng])
        } else {
          const icon = L.divIcon({
            className: 'fgl-location-pin',
            html: `<div style="width:24px;height:24px;border-radius:50%;border:3px solid #f2c94e;background:var(--bg-card,#1a1a2e);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.4);"><div style="width:8px;height:8px;border-radius:50%;background:#f2c94e;"></div></div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          })
          markerRef.current = L.marker([lat, lng], { icon }).addTo(map)
        }
      })

      mapRef.current = map

      // Bestehenden Marker setzen
      if (lat && lon) {
        const icon = L.divIcon({
          className: 'fgl-location-pin',
          html: `<div style="width:24px;height:24px;border-radius:50%;border:3px solid #f2c94e;background:var(--bg-card,#1a1a2e);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.4);"><div style="width:8px;height:8px;border-radius:50%;background:#f2c94e;"></div></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        })
        markerRef.current = L.marker([lat, lon], { icon }).addTo(map)
      }
    })

    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
        markerRef.current = null
      }
    }
  }, [])

  // Marker aktualisieren wenn Koordinaten extern geaendert werden
  useEffect(() => {
    if (!mapRef.current || !lat || !lon) return
    import('leaflet').then((L) => {
      if (markerRef.current) {
        markerRef.current.setLatLng([lat, lon])
      } else {
        const icon = L.divIcon({
          className: 'fgl-location-pin',
          html: `<div style="width:24px;height:24px;border-radius:50%;border:3px solid #f2c94e;background:var(--bg-card,#1a1a2e);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.4);"><div style="width:8px;height:8px;border-radius:50%;background:#f2c94e;"></div></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        })
        markerRef.current = L.marker([lat, lon], { icon }).addTo(mapRef.current)
      }
      mapRef.current.flyTo([lat, lon], 10, { duration: 0.5 })
    })
  }, [lat, lon])

  return (
    <div ref={containerRef} className="w-full rounded-[16px] overflow-hidden" style={{ height: 280 }} />
  )
}

export default function ContactApplyPage() {
  const { t } = useTranslation()
  const { data: session, status: sessionStatus } = useSession()

  // r19.55.96 (Stephan-Spec 2026-08-06): Pro+ Auth-Guard gegen Bot-Spam.
  const [planStatus, setPlanStatus] = useState<'checking' | 'ok' | 'guest' | 'insufficient'>('checking')
  useEffect(() => {
    if (sessionStatus === 'loading') return
    if (!session?.user?.id) { setPlanStatus('guest'); return }
    fetch('/api/subscription').then(r => r.json()).then(j => {
      const plan = j?.plan ?? j?.data?.plan ?? j?.subscription?.plan
      if (plan === 'pro' || plan === 'premium') setPlanStatus('ok')
      else setPlanStatus('insufficient')
    }).catch(() => setPlanStatus('insufficient'))
  }, [session, sessionStatus])

  // r19.55.96: Formular-Load-Timestamp fuer Time-Trap (Bot < 3 s)
  const formStartMs = useRef<number>(Date.now())
  useEffect(() => { formStartMs.current = Date.now() }, [])

  // r19.55.96: Honeypot-State (verstecktes Feld, muss leer bleiben)
  const [websiteHp, setWebsiteHp] = useState('')

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    country: '',
    region: '',
    customRegion: '',
    // 2026-05-29 (R100.54): Rollen reduziert auf "Örtlicher Ansprechpartner" — Default-Wert direkt setzen
    role: 'Örtlicher Ansprechpartner',
    customRole: '',
    focusAreas: [] as string[],
    photoUrl: '',
    bio: '',
    lat: null as number | null,
    lon: null as number | null,
  })
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    const regionInput = form.region === '__custom' ? form.customRegion : form.region
    const roleInput = form.role === '__custom' ? form.customRole : form.role

    if (!form.name.trim() || !form.email.trim() || !roleInput.trim()) {
      setError('Bitte alle Pflichtfelder ausfüllen.')
      return
    }

    if (!form.country.trim()) {
      setError('Bitte ein Land oder einen Kontinent wählen.')
      return
    }

    // Region-Feld faellt auf country zurueck wenn keine Sub-Region zur Auswahl
    // steht (z.B. Italien als Ganzes, USA als Ganzes). So kommt dennoch ein
    // sauberer region-String beim Backend an.
    const finalRegion = (regionInput.trim() || form.country.trim())

    if (!captchaToken) {
      setError('Bitte das Sicherheits-CAPTCHA bestätigen.')
      return
    }

    if (form.lat === null || form.lon === null) {
      setError('Bitte einen Standort auf der Karte wählen oder Koordinaten eingeben.')
      return
    }

    // Schwerpunkte an die Rolle anhaengen — bis Phase 2 eigenes focus_areas-Feld bringt.
    // Format: „Regionalleiter · Fliegen · Technik"
    const finalRole = form.focusAreas.length
      ? `${roleInput.trim()} · ${form.focusAreas.join(' · ')}`
      : roleInput.trim()

    setSubmitting(true)

    try {
      const res = await fetch('/api/contacts/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          website: form.website.trim() || undefined,
          region: finalRegion,
          role: finalRole,
          bio: form.bio.trim() || undefined,
          lat: form.lat,
          lon: form.lon,
          photoUrl: form.photoUrl.trim() || undefined,
          captchaToken,
          // r19.55.96 (Stephan-Spec 2026-08-06): Bot-Schutz — Honeypot muss
          // leer bleiben (echte User fuellen es nicht), formStartMs erlaubt
          // Time-Trap-Check (Bot < 3 s zwischen Load + Submit).
          website_hp: websiteHp,
          formStartMs: formStartMs.current,
        }),
      })

      const data = await res.json()
      if (res.ok && data.success) {
        setSuccess(true)
      } else {
        setError(data.error || 'Fehler beim Einreichen.')
      }
    } catch {
      setError('Netzwerkfehler. Bitte erneut versuchen.')
    }

    setSubmitting(false)
  }

  const update = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }))

  const toggleFocusArea = (value: string) => {
    setForm(f => ({
      ...f,
      focusAreas: f.focusAreas.includes(value)
        ? f.focusAreas.filter(v => v !== value)
        : [...f.focusAreas, value],
    }))
  }

  // Sub-Regionen fuer aktuell gewaehltes Land — leer wenn kein Land oder Land
  // hat keine gepflegten Sub-Regionen (z.B. Italien)
  const subRegions = form.country ? (COUNTRY_REGIONS[form.country] || []) : []
  const hasSubRegions = subRegions.length > 0

  return (
    <div data-award="true">
      <FglHeader />
      <div className="min-h-screen pt-[84px]" style={{ background: 'var(--award-ink)' }}>
        <div className="max-w-[700px] mx-auto px-4 sm:px-6 py-8">

          {/* Back link */}
          <Link href="/contacts" className="inline-flex items-center gap-2 text-[12px] font-bold text-[var(--muted)] hover:text-[var(--accent)] transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Zurück zu Ansprechpartner
          </Link>

          {/* r19.55.96 (Stephan-Spec 2026-08-06): Pro+ Auth-Guard gegen Bot-Spam */}
          {planStatus === 'checking' ? (
            <div className="rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-8 text-center">
              <p className="text-[13px] text-[var(--muted)]">Zugang wird geprüft…</p>
            </div>
          ) : planStatus === 'guest' ? (
            <div className="rounded-[20px] border border-[var(--accent)]/30 bg-[var(--bg-card)] p-8 text-center">
              <Lock className="w-14 h-14 text-[var(--accent)] mx-auto mb-4" />
              <h1 className="text-[22px] font-black tracking-[.04em] uppercase text-[var(--accent)] mb-3">
                Nur für Pro-Piloten
              </h1>
              <p className="text-[13px] text-[var(--muted)] font-bold mb-6 max-w-md mx-auto">
                Um dich als FGL-Ansprechpartner zu bewerben brauchst du einen Pilot-Account mit <strong className="text-[var(--accent)]">Pro-Status</strong> (25+ valide Wertungsflüge).
                <br /><br />
                Bitte einloggen oder registrieren.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link href="/login?callbackUrl=/contacts/apply" className="btn-primary !px-6 !py-3 !text-[13px]">Einloggen</Link>
                <Link href="/register" className="inline-block !px-6 !py-3 !text-[13px] rounded-full border border-[var(--accent)]/40 text-[var(--accent)] font-black tracking-widest uppercase hover:bg-[var(--accent)]/10 transition-colors">Registrieren</Link>
              </div>
            </div>
          ) : planStatus === 'insufficient' ? (
            <div className="rounded-[20px] border border-[var(--accent)]/30 bg-[var(--bg-card)] p-8 text-center">
              <Lock className="w-14 h-14 text-[var(--accent)] mx-auto mb-4" />
              <h1 className="text-[22px] font-black tracking-[.04em] uppercase text-[var(--accent)] mb-3">
                Pro-Status erforderlich
              </h1>
              <p className="text-[13px] text-[var(--muted)] font-bold mb-6 max-w-md mx-auto">
                Ansprechpartner-Bewerbungen sind ab <strong className="text-[var(--accent)]">Pro-Status</strong> (25+ valide Wertungsflüge) möglich. Damit stellen wir sicher, dass nur aktive Piloten die Karte gestalten.
                <br /><br />
                Lade weitere Flüge hoch — sobald du 25 valide Wertungsflüge erreichst, ist die Bewerbung frei.
              </p>
              <Link href="/app/subscription" className="btn-primary !px-6 !py-3 !text-[13px] inline-block">Meinen Fortschritt sehen</Link>
            </div>
          ) : success ? (
            /* Success State */
            <div className="rounded-[20px] border border-[var(--accent)]/30 bg-[var(--bg-card)] p-8 text-center">
              <CheckCircle className="w-16 h-16 text-[var(--accent)] mx-auto mb-4" />
              <h1 className="text-[24px] font-black tracking-[.04em] uppercase text-[var(--accent)] mb-3">
                Danke für deine Bewerbung!
              </h1>
              <p className="text-[14px] text-[var(--muted)] font-bold mb-6 max-w-md mx-auto">
                Deine Bewerbung als FGL-Ansprechpartner wird geprüft. Du erhältst eine Benachrichtigung, sobald sie freigegeben wurde.
              </p>
              <Link href="/contacts" className="btn-primary !px-6 !py-3 !text-[13px] inline-block">
                Zurück zur Übersicht
              </Link>
            </div>
          ) : (
            /* Form */
            <div className="rounded-[20px] border border-[var(--border)] bg-[var(--bg-card)] p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-[12px] bg-[var(--accent)]/10 border border-[var(--accent)]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[var(--accent)]" />
                </div>
                <h1 className="text-[22px] font-black tracking-[.04em] uppercase text-[var(--accent)]">
                  Werde FGL-Ansprechpartner
                </h1>
              </div>
              <p className="text-[13px] text-[var(--muted)] font-bold mb-6">
                Vertrete die FREE GLIDE LEAGUE in deiner Region. Nach Prüfung erscheinst du auf der Ansprechpartner-Karte.
              </p>

              {error && (
                <div className="flex items-start gap-3 mb-6 p-4 rounded-[14px] bg-[rgba(239,68,68,.08)] border border-[rgba(239,68,68,.2)]">
                  <AlertCircle className="w-5 h-5 text-[rgba(239,68,68,.8)] shrink-0 mt-0.5" />
                  <p className="text-[13px] font-bold text-[rgba(239,68,68,.8)]">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* r19.55.96 (Stephan-Spec 2026-08-06): Honeypot-Feld gegen Bots.
                    Visuell + Screenreader komplett unsichtbar (`aria-hidden`,
                    off-screen, `tabIndex=-1`). Echte User fuellen es nicht,
                    Bots fuellen jedes name-Feld → Server verwirft die
                    Bewerbung. */}
                <div
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    top: 'auto',
                    width: 1,
                    height: 1,
                    overflow: 'hidden',
                  }}
                >
                  <label htmlFor="website_hp">Website (nicht ausfuellen)</label>
                  <input
                    id="website_hp"
                    name="website_hp"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    value={websiteHp}
                    onChange={(e) => setWebsiteHp(e.target.value)}
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Dein vollstaendiger Name, wie er auf der Ansprechpartner-Karte erscheint">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => update('name', e.target.value)}
                    placeholder="Vor- und Nachname"
                    className="input"
                    required
                    minLength={2}
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Deine Kontakt-E-Mail fuer Anfragen von Piloten">
                    E-Mail *
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => update('email', e.target.value)}
                    placeholder="deine@email.de"
                    className="input"
                    required
                  />
                </div>

                {/* Phone + Website */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Optional — wird auf deiner Kontaktkarte angezeigt">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="+49 123 456789"
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Optional — Link zu deiner Vereinsseite oder persönlichen Webseite">
                      Website
                    </label>
                    <input
                      type="url"
                      value={form.website}
                      onChange={(e) => update('website', e.target.value)}
                      placeholder="https://..."
                      className="input"
                    />
                  </div>
                </div>

                {/* Land · Kontinent (Parent) */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="In welchem Land oder Kontinent bist du als FGL-Ansprechpartner taetig?">
                    Land · Kontinent *
                  </label>
                  <select
                    value={form.country}
                    onChange={(e) => { update('country', e.target.value); update('region', ''); update('customRegion', '') }}
                    className="input !appearance-auto"
                    required
                  >
                    <option value="">Land oder Kontinent wählen...</option>
                    {COUNTRY_OPTIONS.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Region (Child) — nur wenn Land Sub-Regionen hat */}
                {hasSubRegions && (
                  <div>
                    <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Die Region innerhalb deines Landes — z.B. Bundesland, Kanton, Provinz">
                      Region in {form.country}
                    </label>
                    <select
                      value={form.region}
                      onChange={(e) => update('region', e.target.value)}
                      className="input !appearance-auto"
                    >
                      <option value="">Alle Regionen in {form.country}</option>
                      {subRegions.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                      <option value="__custom">Andere Region…</option>
                    </select>
                    {form.region === '__custom' && (
                      <input
                        type="text"
                        value={form.customRegion}
                        onChange={(e) => update('customRegion', e.target.value)}
                        placeholder="Region eingeben (z.B. neue Sub-Region)..."
                        className="input mt-2"
                      />
                    )}
                  </div>
                )}

                {/* Rolle */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Deine Funktion bei der FGL, z.B. Regionalleiter oder Vereinskontakt">
                    Rolle *
                  </label>
                  <select
                    value={form.role}
                    onChange={(e) => update('role', e.target.value)}
                    className="input !appearance-auto"
                    required
                  >
                    <option value="">Rolle wählen...</option>
                    {ROLES.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                    <option value="__custom">Andere Rolle...</option>
                  </select>
                  {form.role === '__custom' && (
                    <input
                      type="text"
                      value={form.customRole}
                      onChange={(e) => update('customRole', e.target.value)}
                      placeholder="Rolle eingeben..."
                      className="input mt-2"
                      required
                    />
                  )}
                </div>

                {/* Schwerpunkte (Mehrfachauswahl) */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Worin kannst du anderen Piloten am besten helfen? Mehrfachauswahl möglich.">
                    Schwerpunkte <span className="font-normal text-[var(--muted)]">(Mehrfachauswahl)</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {FOCUS_AREAS.map(fa => {
                      const active = form.focusAreas.includes(fa.value)
                      return (
                        <button
                          key={fa.value}
                          type="button"
                          onClick={() => toggleFocusArea(fa.value)}
                          data-tip={fa.tip}
                          className={`px-4 py-2 rounded-full text-[12px] font-bold tracking-[.04em] border transition-colors ${
                            active
                              ? 'bg-[var(--accent)]/15 text-[var(--accent)] border-[var(--accent)]/40'
                              : 'bg-[var(--pill-bg)] text-[var(--muted)] border-[var(--border)] hover:border-[var(--accent)]/30'
                          }`}
                        >
                          {active && <span className="me-1.5">✓</span>}
                          {fa.label}
                        </button>
                      )
                    })}
                  </div>
                </div>

                {/* Profilbild URL */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Optional — Link zu einem Foto von dir (z.B. von deiner Vereins-Website oder einem oeffentlichen Profil). Quadratisch, mind. 200x200 px.">
                    Profilbild-URL <span className="font-normal text-[var(--muted)]">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={form.photoUrl}
                    onChange={(e) => update('photoUrl', e.target.value)}
                    placeholder="https://example.com/mein-foto.jpg"
                    className="input"
                  />
                  <p className="text-[10px] text-[var(--muted)] mt-1">
                    Quadratisch, mindestens 200×200 px. Foto erscheint nach Admin-Freigabe auf deiner Kontaktkarte.
                  </p>
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Kurze Beschreibung ueber dich und dein Engagement im Modellflug">
                    Über mich <span className="text-[var(--muted)] font-normal">(max. 500 Zeichen)</span>
                  </label>
                  <textarea
                    value={form.bio}
                    onChange={(e) => update('bio', e.target.value.slice(0, 500))}
                    placeholder="Erzähle kurz etwas über dich und dein Engagement im Modellflug..."
                    className="input !min-h-[100px] resize-y"
                    rows={4}
                    maxLength={500}
                  />
                  <p className="text-[10px] text-[var(--muted)] mt-1 text-end">
                    {form.bio.length}/500
                  </p>
                </div>

                {/* Standort */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Dein Standort auf der Ansprechpartner-Karte — klicke auf die Karte oder gib Koordinaten ein">
                    Standort * <span className="text-[var(--muted)] font-normal">(Karte klicken oder Koordinaten eingeben)</span>
                  </label>
                  <LocationPicker
                    lat={form.lat}
                    lon={form.lon}
                    onLocationChange={(lat, lon) => setForm(f => ({ ...f, lat, lon }))}
                  />
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-[10px] text-[var(--muted)] mb-1">Breitengrad (Lat)</label>
                      <input
                        type="number"
                        value={form.lat ?? ''}
                        onChange={(e) => update('lat', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="51.1657"
                        className="input !text-[12px]"
                        step="0.0001"
                        min={-90}
                        max={90}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] text-[var(--muted)] mb-1">Längengrad (Lon)</label>
                      <input
                        type="number"
                        value={form.lon ?? ''}
                        onChange={(e) => update('lon', e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="10.4515"
                        className="input !text-[12px]"
                        step="0.0001"
                        min={-180}
                        max={180}
                      />
                    </div>
                  </div>
                </div>

                {/* Sicherheits-CAPTCHA (Cloudflare Turnstile) */}
                <div>
                  <label className="block text-[11px] font-black tracking-[.10em] uppercase text-[var(--muted)] mb-1.5" data-tip="Privatsphärefreundliches CAPTCHA von Cloudflare — keine Daten an Google.">
                    Sicherheitsprüfung *
                  </label>
                  <Turnstile onVerify={(t) => setCaptchaToken(t)} />
                  <p className="text-[10px] text-[var(--muted)] mt-2">
                    Schutz gegen Bots — kein Tracking, keine Daten an Google.
                  </p>
                </div>

                {/* Submit */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={submitting || !captchaToken}
                    className="btn-primary w-full !py-3.5 font-black tracking-[.06em] uppercase disabled:opacity-50"
                    data-tip="Bewerbung wird von einem Admin geprüft — du erhältst eine Benachrichtigung"
                  >
                    {submitting ? 'Wird eingereicht...' : 'Bewerbung einreichen'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
